import React, { useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import UserContext from '../../../common/utils/getContext';
import Form from '../../external_form/ExternalForm';
import { getFormData } from './initData';
import { checkUserToken } from '../utils';
import T, { t } from '../../../common/components/T';
import { dataSender, getData } from '../../../common/utils/router';
import { LangContext } from '../../../common/decorators/Language';
import { useModal2FA } from '../../../widgets/modal2FA/Modal2FA/Modal2FA';
import { API_SERVER_URL } from '../../../common/utils/utils';
import VerifyEmail from '../components/confirm/email/VerifyEmail/VerifyEmail';
import css from './Login.module.scss';

const Login = ({ changePage, afterConfirm, setStepData }) => {
    const { setUserToken, setAlertData, lang, userToken } = useContext(UserContext);
    // const widthDimensions = useWindowDimensions();
    // const [screenWidth, setScreenWidth] = useState('');
    const [attempts, setAttempts] = useState(0);
    const { translation } = useContext(LangContext) || {};
    const [usersByEmail, setUsersByEmail] = useState([]);
    const [sendData, setDataToSend] = useState(false);
    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };
    const setFormDataMainFunc = (key, val, setInitValue) => {
        if (typeof val === 'string' && val.length === 0) {
            setInitValue((pre) =>
                Object.keys(pre).reduce((accum, keyFormData) => {
                    if (key !== keyFormData) accum[keyFormData] = pre[keyFormData];
                    return accum;
                }, {})
            );
        } else {
            setInitValue((pre) => {
                return {
                    ...pre,
                    [key]: val
                };
            });
        }
    };
    const [initValue, setInitValue] = useState();

    const setFormDataMain = useCallback((key, val) => {
        setFormDataMainFunc(key, val, setInitValue);
    }, []);
    // useEffect(() => {
    //     widthDimensions < 768 ? setScreenWidth(true) : setScreenWidth(false);
    // }, [widthDimensions]);
    const data = useMemo(() => getFormData(usersByEmail), [usersByEmail]);
    const interval = useRef(null);
    useEffect(() => {
        if (initValue?.login && validateEmail(initValue.login)) {
            clearTimeout(interval.current);
            interval.current = setTimeout(() => {
                dataSender(
                    `${API_SERVER_URL}/api/v1/users/check-email`,
                    'POST',
                    {
                        email: initValue?.login
                    },
                    (response) => {
                        if (response?.data?.data?.length > 1) {
                            setUsersByEmail(response?.data?.data);
                        } else {
                            setUsersByEmail([]);
                        }
                    },
                    () => {},
                    userToken,
                    lang
                );
            }, 500);
        }
    }, [userToken, lang, initValue?.login]);

    const afterSend = useCallback(
        (data, _, setAllowedSend) => {
            checkUserToken(data, afterConfirm, setAllowedSend, setUserToken, setAlertData, translation);
        },
        [setUserToken, afterConfirm, setAlertData, translation]
    );

    const senderFunc2FA = useCallback(
        (code) => {
            return new Promise((res, rej) => {
                const modernDataToSend = { ...sendData, code };
                dataSender(
                    `${API_SERVER_URL}/api/v1/login`,
                    'POST',
                    modernDataToSend,
                    (response) => {
                        setUserToken(response.data.data.token);
                        res();
                    },
                    (data) => {
                        rej(data?.errors?.code);
                    },
                    userToken,
                    lang
                );
            });
        },
        [sendData, setUserToken, userToken, lang]
    );
    const { Modal2FA, func2Fa } = useModal2FA({ ...sendData, event: 'auth' }, senderFunc2FA);

    const onError = async (data, setAllowedSend, setFieldsErr, dataToSend) => {
        setAllowedSend(true);
        setAlertData(false);
        if (dataToSend?.login) {
            const data = await getData(`/api/v1/auth-attempts?login=${dataToSend?.login}`, userToken);
            if (data && data.attempts) {
                setAttempts(+data.attempts);
            }
        }
        if (data?.errors?.password) {
            setAlertData({ type: 'error', cont: data.message });
            setFieldsErr(data.errors);
            return;
        }
        if (data?.errors && data?.errors[0] && data.errors[0].code === 'unverifiedEmail') {
            return setStepData({
                component: VerifyEmail,
                props: {
                    setStepData,
                    data: { ...data, userData: { email: data?.data?.email } }
                },
                layoutStyle: 'authBlock_lg'
            });
        }
        if (data?.errors && data?.errors['userBlocked']) {
            setAlertData({
                type: 'error',
                title: t('error_block_title', 'Доступ к кабинету был временно заблокирован!', '/authentication', translation),
                cont: data?.errors['userBlocked']?.[0]
            });
            return;
        }
        if (data?.errors?.code) {
            func2Fa(dataToSend);
            setFieldsErr(data.errors);
            return;
        }
        if (data?.errors && data?.errors['userBlocked']) {
            setAlertData({
                type: 'error',
                title: t('error_block_title', 'Доступ к кабинету был временно заблокирован!', '/authentication', translation),
                cont: data?.errors['userBlocked']?.[0]
            });
            return;
        }
        setFieldsErr(data.errors);
    };

    const sender = async (dataToSend, setAllowedSend, setFieldsErr) => {
        setDataToSend(dataToSend);
        const modernDataToSend =
            dataToSend && Object.keys(dataToSend).length
                ? Object.keys(dataToSend).reduce((accum, key) => {
                      if (key == 'user_id') {
                          accum[key] = dataToSend[key]?.id;
                      } else {
                          accum[key] = dataToSend[key];
                      }

                      return accum;
                  }, {})
                : dataToSend;
        dataSender(
            `${API_SERVER_URL}/api/v1/login`,
            'POST',
            modernDataToSend,
            (response) => {
                setAllowedSend(true);
                setFieldsErr({});
                afterSend(response.data, setFieldsErr, setAllowedSend, dataToSend);
            },
            async (data) => {
                await onError(data, setAllowedSend, setFieldsErr, dataToSend);
            },
            userToken,
            lang
        );
    };

    return (
        <>
            <Form
                formName={'login'}
                data={{ data }}
                captcha={{ alias: 'user::auth', attempts }}
                sendData={{ sender }}
                // add={[
                //     {
                //         position: !screenWidth ? 'bottom' : 'middle',
                //         comp: ForgotPass
                //     }
                // ]}
                cancelFuncs={{
                    web: {
                        onClick: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            changePage('password-recover');
                        }
                    }
                }}
                setFormDataMain={setFormDataMain}
                formDataMain={initValue}
                buttonText={<T textName='log_enter-v1-2' defaultStr='Войти в аккаунт' disabled={true} />}
                hoverColor={{
                    hover: 'var(--blueColor)',
                    blur: 'var(--greyDarkColor)'
                }}
                message={{ error: false }}
                buttonNotShow={false}
                cancelText={<T textName='forgot-password' defaultStr='Забыли пароль' />}
                formStyles={{
                    password: {
                        wrapper: { className: css.passwordWrapper }
                    }
                }}
            />
            {Modal2FA}
        </>
    );
};

export default Login;
